import { defineMutator, defineMutators, type Transaction } from "@rocicorp/zero"
import {
	clearProjectNodeInputValue,
	defaultProjectPythonVersion,
	projectJSDependencyNamePattern,
	projectJSExactVersionPattern,
	pythonIdentifierPattern,
	setProjectNodeInputValue,
	type ProjectNodeInputs,
} from "../runtime"
import { v7 as uuidv7 } from "uuid"
import z from "zod"
import { assertIsLoggedIn } from "./auth"
import { type schema, zql } from "./schema"

export type MutatorTx = Transaction<typeof schema>

const pythonVersionSchema = z.string().trim().min(1)
const pythonDependencySchema = z.object({
	name: z
		.string()
		.trim()
		.regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/),
	version: z.string().trim().min(1),
})
const jsDependencySchema = z.object({
	name: z.string().trim().regex(projectJSDependencyNamePattern),
	version: z.string().trim().regex(projectJSExactVersionPattern),
})

const jsonValueSchema = z.json()
const pythonIdentifierSchema = z.string().regex(pythonIdentifierPattern)
const nodeInputSchema = z
	.object({
		type: z.string().optional(),
		required: z.boolean().optional(),
		description: z.string().optional(),
		value: jsonValueSchema.optional(),
	})
	.strict()
const nodeInputsSchema = z.record(pythonIdentifierSchema, nodeInputSchema)
const nodeOutputsSchema = z.array(pythonIdentifierSchema)

export const mutators = defineMutators({
	projects: {
		create: defineMutator(
			z.object({
				id: z.string(),
				name: z.string(),
				description: z.string(),
				python_version: z.string().optional(),
				created_at: z.number(),
				updated_at: z.number(),
			}),
			async ({
				tx,
				ctx,
				args: { id, name, description, python_version, created_at, updated_at },
			}) => {
				assertIsLoggedIn(ctx)

				// Create the project
				await tx.mutate.projects.insert({
					id,
					name,
					description,
					python_version: python_version ?? defaultProjectPythonVersion,
					python_dependencies: [],
					uv_lock: null,
					js_dependencies: [],
					bun_lock: null,
					bun_lock_hash: null,
					js_runtime_version: 1,
					created_at,
					updated_at,
					created_by_user_id: ctx.userId,
					settings: "",
					share_enabled: false,
				})

				// Add creator as owner in project_members
				await tx.mutate.project_members.insert({
					id: uuidv7(),
					project_id: id,
					user_id: ctx.userId,
					role: "owner",
					created_at,
					updated_at,
				})
			},
		),
		delete: defineMutator(
			z.object({
				project_id: z.string(),
			}),
			async ({ tx, ctx, args: { project_id } }) => {
				assertIsLoggedIn(ctx)
				// Permission check happens in server mutator

				const [edges, geometries, nodes, invitations, members] =
					await Promise.all([
						tx.run(zql.project_edges.where("project_id", "=", project_id)),
						tx.run(zql.geometries.where("project_id", "=", project_id)),
						tx.run(zql.project_nodes.where("project_id", "=", project_id)),
						tx.run(
							zql.project_invitations.where("project_id", "=", project_id),
						),
						tx.run(zql.project_members.where("project_id", "=", project_id)),
					])

				for (const edge of edges) {
					await tx.mutate.project_edges.delete({ id: edge.id })
				}
				for (const geometry of geometries) {
					await tx.mutate.geometries.delete({ id: geometry.id })
				}
				for (const node of nodes) {
					await tx.mutate.project_nodes.delete({ id: node.id })
				}
				for (const invitation of invitations) {
					await tx.mutate.project_invitations.delete({ id: invitation.id })
				}
				for (const member of members) {
					await tx.mutate.project_members.delete({ id: member.id })
				}
				await tx.mutate.projects.delete({
					id: project_id,
				})
			},
		),

		// Update project share settings
		updateShareSettings: defineMutator(
			z.object({
				project_id: z.string(),
				share_enabled: z.boolean().optional(),
				share_token: z.string().optional(),
			}),
			async ({ tx, ctx, args: { project_id, share_enabled, share_token } }) => {
				assertIsLoggedIn(ctx)
				// Permission check happens in server mutator

				const updates: Record<string, unknown> = {
					id: project_id,
					updated_at: Date.now(),
				}

				if (share_enabled !== undefined) {
					updates.share_enabled = share_enabled
				}

				if (share_token !== undefined) {
					updates.share_token = share_token
				}

				await tx.mutate.projects.update(updates as never)
			},
		),

		updateRuntimeSettings: defineMutator(
			z.object({
				project_id: z.string(),
				python_version: pythonVersionSchema,
			}),
			async ({ tx, ctx, args: { project_id, python_version } }) => {
				assertIsLoggedIn(ctx)

				await tx.mutate.projects.update({
					id: project_id,
					python_version,
					updated_at: Date.now(),
				})
			},
		),

		updateDependencies: defineMutator(
			z.object({
				project_id: z.string(),
				python_dependencies: z.array(pythonDependencySchema),
				uv_lock: z.string().nullable(),
			}),
			async ({
				tx,
				ctx,
				args: { project_id, python_dependencies, uv_lock },
			}) => {
				assertIsLoggedIn(ctx)
				await tx.mutate.projects.update({
					id: project_id,
					python_dependencies,
					uv_lock,
					updated_at: Date.now(),
				})
			},
		),

		updateJSDependencies: defineMutator(
			z.object({
				project_id: z.string(),
				js_dependencies: z.array(jsDependencySchema),
				bun_lock: z.string().nullable(),
				bun_lock_hash: z
					.string()
					.regex(/^[a-f0-9]{64}$/)
					.nullable(),
				js_runtime_version: z.number().int().positive(),
			}),
			async ({
				tx,
				ctx,
				args: {
					project_id,
					js_dependencies,
					bun_lock,
					bun_lock_hash,
					js_runtime_version,
				},
			}) => {
				assertIsLoggedIn(ctx)
				await tx.mutate.projects.update({
					id: project_id,
					js_dependencies,
					bun_lock,
					bun_lock_hash,
					js_runtime_version,
					updated_at: Date.now(),
				})
			},
		),
	},

	runtimeOutputs: {
		record: defineMutator(
			z.object({
				id: z.string(),
				project_id: z.string(),
				node_id: z.string(),
				serialized_data: z.string(),
				created_at: z.number(),
			}),
			async ({
				tx,
				ctx,
				args: { id, project_id, node_id, serialized_data, created_at },
			}) => {
				assertIsLoggedIn(ctx)
				await tx.mutate.geometries.insert({
					id,
					completed_at: created_at,
					created_at,
					metadata: { source: "origin_runtime" },
					parameters: {},
					project_id,
					project_node_id: node_id,
					serialized_data,
					status: "completed",
					type: "runtime_output",
					user_id: ctx.userId,
				})
			},
		),
	},

	nodes: {
		add: defineMutator(
			z.object({
				id: z.string().optional(),
				project_id: z.string(),
				label: z.string(),
				position: z.object({ x: z.number(), y: z.number() }).optional(),
				metadata: z.record(z.string(), z.any()).optional(),
				type: z.enum(["python", "react"]),
				code: z.string(),
				inputs: nodeInputsSchema,
				outputs: nodeOutputsSchema,
			}),
			async ({
				tx,
				ctx,
				args: {
					id,
					project_id,
					label,
					position,
					metadata,
					type,
					code,
					inputs,
					outputs,
				},
			}) => {
				assertIsLoggedIn(ctx)
				// Permission check happens in server mutator
				if (type === "python" && !code.trim())
					throw new Error("Python node code is required")
				if (type === "python" && outputs.length === 0)
					throw new Error("Python nodes require at least one output")

				await tx.mutate.project_nodes.insert({
					id: id ?? uuidv7(),
					project_id,
					label: label,
					position: position ?? { x: 0, y: 0 },
					metadata: JSON.stringify(metadata ?? {}),
					type,
					code,
					inputs,
					outputs,
					created_at: Date.now(),
					updated_at: Date.now(),
				})
			},
		),
		update: defineMutator(
			z.object({
				project_id: z.string(),
				node_id: z.string(),
				position: z.object({ x: z.number(), y: z.number() }).optional(),
				metadata: z.record(z.string(), z.any()).optional(),
				label: z.string().optional(),
			}),
			async ({
				tx,
				ctx,
				args: { project_id, node_id, position, metadata, label },
			}) => {
				assertIsLoggedIn(ctx)
				// Permission check happens in server mutator

				const currentNode = await tx.run(
					zql.project_nodes.where("id", "=", node_id).one(),
				)

				let finalMetadata =
					(currentNode?.metadata as Record<string, unknown>) ?? null
				if (metadata && Object.keys(metadata).length > 0) {
					finalMetadata = { ...finalMetadata, ...metadata }
				}

				const updates: Record<string, unknown> = {
					id: node_id,
					project_id,
					updated_at: Date.now(),
					...(position !== undefined && { position }),
					...(finalMetadata !== undefined && {
						metadata: finalMetadata,
					}),
					...(label !== undefined && { label }),
				}

				await tx.mutate.project_nodes.update(updates as never)
			},
		),
		updateDefinition: defineMutator(
			z.object({
				project_id: z.string(),
				node_id: z.string(),
				code: z.string(),
				inputs: nodeInputsSchema,
				label: z.string(),
				metadata: z.record(z.string(), z.any()).optional(),
				outputs: nodeOutputsSchema,
			}),
			async ({
				tx,
				ctx,
				args: { project_id, node_id, code, inputs, label, metadata, outputs },
			}) => {
				assertIsLoggedIn(ctx)
				const node = await tx.run(
					zql.project_nodes.where("id", "=", node_id).one(),
				)
				if (!node) throw new Error("Node not found")
				if (node.type === "python" && !code.trim())
					throw new Error("Python node code is required")
				if (node.type === "python" && outputs.length === 0)
					throw new Error("Python nodes require at least one output")

				let nextMetadata = node.metadata
				if (metadata) {
					let currentMetadata: Record<string, unknown> = {}
					if (typeof node.metadata === "string") {
						try {
							const parsed = JSON.parse(node.metadata) as unknown
							if (
								parsed &&
								typeof parsed === "object" &&
								!Array.isArray(parsed)
							)
								currentMetadata = parsed as Record<string, unknown>
						} catch {
							// Replace malformed legacy metadata with the derived fields.
						}
					} else if (
						node.metadata &&
						typeof node.metadata === "object" &&
						!Array.isArray(node.metadata)
					) {
						currentMetadata = node.metadata as Record<string, unknown>
					}
					nextMetadata = JSON.stringify({ ...currentMetadata, ...metadata })
				}

				await tx.mutate.project_nodes.update({
					id: node_id,
					code,
					inputs,
					label,
					metadata: nextMetadata,
					outputs,
					updated_at: Date.now(),
				})

				const edges = await tx.run(
					zql.project_edges.where("project_id", "=", project_id),
				)
				for (const edge of edges) {
					const invalidSource =
						edge.source_node_id === node_id &&
						(typeof edge.source_handle !== "string" ||
							!outputs.includes(edge.source_handle))
					const invalidTarget =
						edge.target_node_id === node_id &&
						(typeof edge.target_handle !== "string" ||
							!Object.hasOwn(inputs, edge.target_handle))
					if (invalidSource || invalidTarget) {
						await tx.mutate.project_edges.delete({ id: edge.id })
					}
				}
			},
		),
		setInputValue: defineMutator(
			z.object({
				project_id: z.string(),
				node_id: z.string(),
				input_id: pythonIdentifierSchema,
				value: jsonValueSchema,
			}),
			async ({ tx, ctx, args: { node_id, input_id, value } }) => {
				assertIsLoggedIn(ctx)
				const node = await tx.run(
					zql.project_nodes.where("id", "=", node_id).one(),
				)
				const inputs = (node?.inputs ?? {}) as ProjectNodeInputs
				await tx.mutate.project_nodes.update({
					id: node_id,
					inputs: setProjectNodeInputValue(inputs, input_id, value),
					updated_at: Date.now(),
				})
			},
		),
		clearInputValue: defineMutator(
			z.object({
				project_id: z.string(),
				node_id: z.string(),
				input_id: pythonIdentifierSchema,
			}),
			async ({ tx, ctx, args: { node_id, input_id } }) => {
				assertIsLoggedIn(ctx)
				const node = await tx.run(
					zql.project_nodes.where("id", "=", node_id).one(),
				)
				const inputs = (node?.inputs ?? {}) as ProjectNodeInputs
				await tx.mutate.project_nodes.update({
					id: node_id,
					inputs: clearProjectNodeInputValue(inputs, input_id),
					updated_at: Date.now(),
				})
			},
		),
		delete: defineMutator(
			z.object({
				node_id: z.string(),
				project_id: z.string(),
			}),
			async ({ tx, ctx, args: { node_id } }) => {
				assertIsLoggedIn(ctx)
				// Permission check happens in server mutator

				await tx.mutate.project_nodes.delete({
					id: node_id,
				})
			},
		),
	},

	edges: {
		add: defineMutator(
			z.object({
				id: z.string().optional(),
				project_id: z.string(),
				source_node_id: z.string(),
				source_handle: pythonIdentifierSchema,
				target_node_id: z.string(),
				target_handle: pythonIdentifierSchema,
				metadata: z.record(z.string(), z.any()).optional(),
			}),
			async ({
				tx,
				ctx,
				args: {
					id,
					project_id,
					source_node_id,
					source_handle,
					target_node_id,
					target_handle,
					metadata,
				},
			}) => {
				assertIsLoggedIn(ctx)
				// Permission check happens in server mutator
				const [sourceNode, targetNode] = await Promise.all([
					tx.run(zql.project_nodes.where("id", "=", source_node_id).one()),
					tx.run(zql.project_nodes.where("id", "=", target_node_id).one()),
				])
				if (!sourceNode || !targetNode) throw new Error("Node not found")
				if (
					!Array.isArray(sourceNode.outputs) ||
					!sourceNode.outputs.includes(source_handle)
				)
					throw new Error(`Output ${source_handle} not found`)
				const inputs = targetNode.inputs as ProjectNodeInputs
				if (!Object.hasOwn(inputs, target_handle))
					throw new Error(`Input ${target_handle} not found`)

				await tx.mutate.project_edges.insert({
					id: id ?? uuidv7(),
					project_id,
					source_node_id,
					source_handle,
					target_node_id,
					target_handle,
					metadata: metadata ?? {},
					created_at: Date.now(),
					updated_at: Date.now(),
				})
			},
		),
		delete: defineMutator(
			z.object({
				project_id: z.string(),
				edge_id: z.string(),
			}),
			async ({ tx, ctx, args: { edge_id } }) => {
				assertIsLoggedIn(ctx)
				// Permission check happens in server mutator

				await tx.mutate.project_edges.delete({
					id: edge_id,
				})
			},
		),
	},

	// Invitation mutators
	invitations: {
		create: defineMutator(
			z.object({
				id: z.string(),
				project_id: z.string(),
				email: z.string(),
				role: z.enum(["editor", "viewer"]),
				token: z.string(),
				expires_at: z.number(),
			}),
			async ({
				tx,
				ctx,
				args: { id, project_id, email, role, token, expires_at },
			}) => {
				assertIsLoggedIn(ctx)
				// Permission check happens in server mutator

				await tx.mutate.project_invitations.insert({
					id,
					project_id,
					email: email.toLowerCase(),
					role,
					token,
					status: "pending",
					invited_by_user_id: ctx.userId,
					created_at: Date.now(),
					expires_at: expires_at,
				})
			},
		),

		accept: defineMutator(
			z.object({
				invitation_id: z.string(),
				member_id: z.string(),
			}),
			async ({ tx, ctx, args: { invitation_id } }) => {
				assertIsLoggedIn(ctx)
				// Server mutator handles: email match, expiry check, create member

				// Update invitation status
				await tx.mutate.project_invitations.update({
					id: invitation_id,
					status: "accepted",
				})
			},
		),

		decline: defineMutator(
			z.object({
				invitation_id: z.string(),
			}),
			async ({ tx, ctx, args: { invitation_id } }) => {
				assertIsLoggedIn(ctx)

				await tx.mutate.project_invitations.update({
					id: invitation_id,
					status: "declined",
				})
			},
		),

		cancel: defineMutator(
			z.object({
				invitation_id: z.string(),
				project_id: z.string(),
			}),
			async ({ tx, ctx, args: { invitation_id } }) => {
				assertIsLoggedIn(ctx)
				// Permission check happens in server mutator

				await tx.mutate.project_invitations.delete({
					id: invitation_id,
				})
			},
		),
	},

	// Member mutators
	members: {
		add: defineMutator(
			z.object({
				id: z.string(),
				project_id: z.string(),
				user_id: z.string(),
				role: z.enum(["owner", "editor", "viewer"]),
			}),
			async ({ tx, ctx, args: { id, project_id, user_id, role } }) => {
				assertIsLoggedIn(ctx)
				// Permission check happens in server mutator

				await tx.mutate.project_members.insert({
					id,
					project_id,
					user_id,
					role,
					created_at: Date.now(),
					updated_at: Date.now(),
				})
			},
		),

		updateRole: defineMutator(
			z.object({
				member_id: z.string(),
				project_id: z.string(),
				role: z.enum(["editor", "viewer"]),
			}),
			async ({ tx, ctx, args: { member_id, role } }) => {
				assertIsLoggedIn(ctx)
				// Permission check happens in server mutator

				await tx.mutate.project_members.update({
					id: member_id,
					role,
					updated_at: Date.now(),
				})
			},
		),

		remove: defineMutator(
			z.object({
				member_id: z.string(),
				project_id: z.string(),
			}),
			async ({ tx, ctx, args: { member_id } }) => {
				assertIsLoggedIn(ctx)
				// Permission check happens in server mutator

				await tx.mutate.project_members.delete({
					id: member_id,
				})
			},
		),
	},
})
